import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { buildPublicOrigin, resolveApiBaseUrl } from '@minibook/shared';
import { BOOKING_HOST_PATTERN, getSlugFromHost, getSlugFromQuery, isBookingSubdomain } from '../../shared/bookingHost';
import BookingPage from './BookingPage';

/**
 * Редирект только если открыли прод-URL (m*.beclients.ru), а загружена dev-сборка — ведём на dev-хост.
 */
function getCorrectBookingOrigin(hostname: string, apiUrl: string): string | null {
  const match = hostname.match(BOOKING_HOST_PATTERN);
  if (!match) return null;
  const publicCode = match[1];
  const isDevBuild = typeof apiUrl === 'string' && (apiUrl.includes('api-dev') || apiUrl.includes('dev.beclients'));
  const isDevHost = hostname.startsWith('dev-m');
  if (isDevBuild && !isDevHost) {
    return buildPublicOrigin({ publicCode, mode: 'development' });
  }
  return null;
}

/**
 * Обработка корня /:
 * - m5472.beclients.ru/ или dev-m5472.beclients.ru/ → страница записи (честный URL)
 * - localhost/?code=5472 → страница записи (для проверки честного URL локально)
 * - localhost/ без code → /dev
 * - остальное → 404
 */
export default function RootRedirect() {
  const navigate = useNavigate();
  const slugFromHost = getSlugFromHost();
  const slugFromQuery = getSlugFromQuery();
  const isLocalhost = typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

  useEffect(() => {
    const hostname = window.location.hostname;
    const apiUrl = resolveApiBaseUrl({
      apiUrl: (import.meta as any).env?.VITE_API_URL,
      isDev: !!(import.meta as any).env?.DEV,
    });

    const correctOrigin = getCorrectBookingOrigin(hostname, apiUrl);
    if (correctOrigin) {
      window.location.replace(`${correctOrigin}${window.location.pathname}${window.location.search}${window.location.hash}`);
      return;
    }

    if (isBookingSubdomain()) {
      return;
    }
    // localhost без ?code= → страница подсказок
    if (isLocalhost && !slugFromQuery) {
      navigate('/dev', { replace: true });
      return;
    }
    if (!slugFromQuery) {
      navigate('/404', { replace: true });
    }
  }, [navigate, isLocalhost, slugFromQuery]);

  // Принудительно короткий URL в адресной строке, когда показываем запись с корня
  useEffect(() => {
    if (!(slugFromHost || slugFromQuery) || typeof window === 'undefined') return;
    const canonical = slugFromQuery
      ? `${window.location.origin}/?code=${slugFromQuery}`
      : `${window.location.origin}/`;
    if (window.location.pathname !== '/' || window.location.href !== canonical) {
      window.history.replaceState(null, '', canonical);
    }
  }, [slugFromHost, slugFromQuery]);

  // Честный URL: поддомен (m*/dev-m*) или localhost/?code=5472
  if (slugFromHost || slugFromQuery) {
    return <BookingPage />;
  }

  return null;
}
